import React, { useMemo, useState, useCallback } from "react";
import { Form, InputGroup, Button, Badge } from "react-bootstrap";
import { useCompaniesWithoutSubscription } from "../../hooks/useCompaniesWithoutSubscription";
import DataTable from "components/common/DataTable";
import { createColumn } from "components/common/DataTable/utils";
import InitialLoader from "components/InitilalLoader";
import { formatDate } from "utils/date";

const CompaniesWithoutSubscriptionList = ({ onViewDetails }) => {
  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    limit: 20,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const { companies, pagination, isLoading, error } = useCompaniesWithoutSubscription(filters);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  }, []);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    handleFilterChange("search", searchTerm);
  }, [searchTerm, handleFilterChange]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    handleFilterChange("search", "");
  }, [handleFilterChange]);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setFilters((prev) => ({ ...prev, limit: newSize, page: 1 }));
  }, []);

  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: filters.limit,
      pageIndex: filters.page - 1,
      pageCount: pagination?.pages || 1,
      totalCount: pagination?.total || 0,
      manual: true,
      onPageChange: handlePageChange,
      onPageSizeChange: handlePageSizeChange,
    }),
    [filters.limit, filters.page, pagination?.pages, pagination?.total, handlePageChange, handlePageSizeChange]
  );

  const columns = useMemo(
    () => [
      createColumn({
        id: "companyName",
        header: "Company Name",
        accessorKey: "companyName",
        cell: ({ row }) => row.original.companyName || row.original.companyId,
        enableSorting: true,
      }),
      createColumn({
        id: "companyEmail",
        header: "Company Email",
        accessorKey: "companyEmail",
        cell: ({ row }) => row.original.companyEmail || "N/A",
        enableSorting: true,
      }),
      createColumn({
        id: "paymentStatus",
        header: "Payment Status",
        accessorKey: "paymentStatus",
        cell: ({ row }) => (
          <Badge bg="secondary">
            {row.original.paymentStatus || "No Subscription"}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "activate",
        header: "Status",
        accessorKey: "activate",
        cell: ({ row }) => (
          <Badge bg={row.original.activate ? "success" : "secondary"}>
            {row.original.activate ? "Active" : "Inactive"}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "companyCreatedOn",
        header: "Company Created On",
        accessorKey: "companyCreatedOn",
        cell: ({ row }) => {
          const date = row.original.companyCreatedOn || row.original.createdAt || row.original.createdOn;
          return date ? formatDate(date) : "N/A";
        },
        enableSorting: true,
      }),
    ],
    []
  );

  if (error) {
    return <div className="text-danger">Error loading companies: {error.message}</div>;
  }

  const tableData = companies || [];

  return (
    <div>
   

      <div className="mb-3">
        <Form onSubmit={handleSearch} className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by company name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button variant="outline-secondary" type="submit">
              Search
            </Button>
            {searchTerm && (
              <Button variant="outline-secondary" onClick={handleClearSearch}>
                Clear
              </Button>
            )}
          </InputGroup>
        </Form>
      </div>

      <DataTable
        data={tableData}
        columns={columns}
        loading={isLoading}
        pagination={paginationConfig}
        sorting={{ enabled: true, manual: false }}
        filtering={{ enabled: false }}
        onRowClick={onViewDetails ? (row) => onViewDetails(row.companyId, "pending-subscriptions") : undefined}
        keyField="companyId"
        striped
        bordered
        hover
        responsive
        emptyMessage="No companies without subscriptions found"
      />
    </div>
  );
};

export default CompaniesWithoutSubscriptionList;

